<script lang="ts" setup>
import type { HTMLAttributes } from "vue"
import { computed } from "vue"
import { useRoute } from "vue-router"
import { ThemeButton } from "~/components/theme"
import { useLocation } from "~/composables/locations"
import { useMedia } from "~/composables/media"
import { useScrolled } from "~/composables/scroll"
import { cn } from "~/lib/utils"
import { NavItem, NavMenuVariants } from "."

const { item, variant, class: cl } = defineProps<{
    item: NavItem
    variant?: NavMenuVariants["variant"]
    class?: HTMLAttributes["class"]
}>()

const emits = defineEmits<{
    (e: "select", sectionId?: string): void
    (e: "open"): void
    (e: "close"): void
}>()

const route = useRoute()
const { hasOneLocation } = useLocation()
const scrolled = useScrolled()
const { isPhone } = useMedia()

const sectionRoutes = new Set([
    "menus",
    "large-party-reservations",
    "private-dining",
    "catering",
    "hours-location",
    "gift-cards-rewards",
    "merch",
    "faq"
])

function getSectionIdFromRoute(to: NavItem["to"]): string | null {
    if (!to) return null

    if (typeof to !== "string") {
        if (to.hash) {
            return to.hash.replace(/^#/, "") || null
        }
        const path = to.path
        if (!path) return null
        const segments = path.split("/").filter(Boolean)
        return [...segments].reverse().find(segment => sectionRoutes.has(segment)) ?? null
    }

    if (to.includes("#")) {
        return to.split("#")[1] || null
    }

    const segments = to.split("/").filter(Boolean)
    return [...segments].reverse().find(segment => sectionRoutes.has(segment)) ?? null
}

function onInternalClick(event: MouseEvent, navigate: (e?: MouseEvent) => void) {
    if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0) return

    navigate(event)
    emits("select", getSectionIdFromRoute(item.to) ?? undefined)
}

const oneLocationRouteSlug = computed(() => {
    const legalPages = ["privacy-policy", "terms-of-use", "accessibility-statement"]
    return hasOneLocation.value && legalPages.includes(route.name as string)
})

const itemClass = computed(() => {
    return cn(
        !item.button && !item.emphasize && "text-nav-bar-items uppercase",
        !item.button && variant === "vertical" && "px-0",
        item.button && !item.emphasize && "border-nav-bar-items bg-transparent text-nav-bar-items hover:bg-nav-bar-items-background-hover hover:text-nav-bar-items-text-hover",
        item.button && !item.emphasize && isPhone && "px-1",
        item.button && item.emphasize && "bg-nav-bar-main-cta-background text-nav-bar-main-cta-text hover:bg-nav-bar-main-cta-background/80",
        (scrolled.value || oneLocationRouteSlug.value) && item.emphasize && "bg-nav-bar-main-cta-background-scrolled text-nav-bar-main-cta-text-scrolled hover:bg-nav-bar-main-cta-background-scrolled/80"
    )
})

function isExternal(to: NavItem["to"]) {
    return typeof to === "string" && to.startsWith("https")
}

function handleOpen(value: boolean) {
    if (value) {
        emits("open")
    } else {
        emits("close")
    }
}
</script>

<template>
    <div v-if="item.modal">
        <Component
            :is="item.modal.component"
            v-bind="item.modal.props"
            @update:open="handleOpen"
        >
            <RouterLink
                v-if="item.to && !isExternal(item.to)"
                v-slot="{ navigate }"
                :to="item.to"
                tabindex="0"
                custom
                @click="emits('select')"
            >
                <ThemeButton
                    :variant="!item.button ? 'link' : item.emphasize ? 'default' : 'outline'"
                    size="sm"
                    :class="cn(itemClass, cl)"
                    @click="navigate"
                >
                    <span>{{ item.name }}</span>
                </ThemeButton>
            </RouterLink>
            <ThemeButton
                v-else-if="item.to && isExternal(item.to)"
                :variant="!item.button ? 'link' : item.emphasize ? 'default' : 'outline'"
                size="sm"
                :class="cn(itemClass, cl)"
                as-child
            >
                <a
                    :href="item.to as string"
                    target="_blank"
                    rel="noopener noreferrer"
                    tabindex="0"
                    :aria-label="`Open external link in new tab to ${item.name}`"
                    @click="emits('select')"
                >
                    <span>{{ item.name }}</span>
                </a>
            </ThemeButton>
            <ThemeButton
                v-else
                :variant="!item.button ? 'link' : item.emphasize ? 'default' : 'outline'"
                size="sm"
                :class="cn(itemClass, cl)"
                tabindex="0"
            >
                <span>{{ item.name }}</span>
            </ThemeButton>
        </Component>
    </div>
    <ThemeButton
        v-else-if="isExternal(item.to)"
        :variant="!item.button ? 'link' : item.emphasize ? 'default' : 'outline'"
        size="sm"
        :class="cn(itemClass, cl)"
        as-child
    >
        <a
            :href="item.to as string"
            target="_blank"
            rel="noopener noreferrer"
            tabindex="0"
            :aria-label="`Open external link in new tab to ${item.name}`"
            @click="emits('select')"
        >
            <span>{{ item.name }}</span>
        </a>
    </ThemeButton>
    <RouterLink
        v-else
        v-slot="{ href, navigate }"
        :to="item.to!"
        custom
    >
        <ThemeButton
            :variant="!item.button ? 'link' : item.emphasize ? 'default' : 'outline'"
            size="sm"
            :class="cn(itemClass, cl)"
            as-child
        >
            <a
                :href="href"
                :aria-label="`Navigate to ${item.name}`"
                @click="onInternalClick($event, navigate)"
            >
                <span>{{ item.name }}</span>
            </a>
        </ThemeButton>
    </RouterLink>
</template>
